import * as React from "react";
import { withStores } from "src/stores";
import { PropertyFiltersQuery } from "../../../../../__generated__/api";
import { PropertyFiltersComponentProps } from "../typings";
import { PropertyFiltersStore } from "src/stores/PropertyFiltersStore";
import Options = PropertyFiltersQuery._Options;
type RangeType = Options["range"];

type OnChange = PropertyFiltersComponentProps["onChange"];

const Range = (range: RangeType) => ({
  isEqual: (otherRange: RangeType) => range?.min === otherRange?.min && range?.max === otherRange?.max,
  isSmaller: (otherRange: RangeType) => Boolean(range?.min && otherRange?.max && range.min >= otherRange.max),
  isLarger: (otherRange: RangeType) => Range(otherRange).isSmaller(range),
});

/**
 *
 * @param { Options } selectedOption
 *
 * Creates an option comparition method according to isSelected value of the selected option.
 * If it's selected all option with a smaller range will return true, if it's not so all the larger options
 *
 */
const buildDependentOptionComparator = (selectedOption: Options) => {
  return (option: Options) => {
    const isSmallerOption = Range(selectedOption.range).isSmaller(option.range);
    const isLargerOption = Range(selectedOption.range).isLarger(option.range);
    const isDependentOption = selectedOption.isSelected ? isSmallerOption : isLargerOption;
    const isSameRange = Range(selectedOption.range).isEqual(option.range);

    return !isSameRange && isDependentOption;
  };
};

/**
 *
 * @param { OnChange } currentHandle
 * @param { PropertyFiltersStore } propertyFilters
 *
 * Create a option handler that propagate the change on all related options,
 * and persist the onChange handler if the user provide one.
 *
 */
const createHandle = (propertyFilters: PropertyFiltersStore): OnChange => {
  return (filterName, selectedOption) => {
    const shouldUpdateOption = buildDependentOptionComparator(selectedOption);

    propertyFilters
      .getOptions(filterName)
      .filter(shouldUpdateOption)
      .forEach(({ optionValue }) =>
        propertyFilters.setFilterStatus(filterName, optionValue!, selectedOption.isSelected)
      );
  };
};

type ChildProps = PropertyFiltersComponentProps;
type Child = React.ReactElement<ChildProps>;

export const RangeCheckboxGroup = withStores("propertyFilters")(({ propertyFilters, children }) => {
  const overrideProps = React.useCallback(
    (child: Child) => React.cloneElement(child, { onChange: createHandle(propertyFilters) }),
    [propertyFilters]
  );

  return <div>{React.Children.map(children, overrideProps)}</div>;
});

export default RangeCheckboxGroup;
